import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bell, Send, Users, History } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export const NotificationsTab = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Fetch token count
  const { data: tokenCount } = useQuery({
    queryKey: ["push-token-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("push_tokens")
        .select("*", { count: "exact", head: true });

      if (error) throw error;
      return count || 0;
    },
  });

  // Fetch notification history
  const { data: notifications, refetch } = useQuery({
    queryKey: ["notifications-log"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications_log")
        .select("*")
        .order("sent_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    },
  });

  const handleSendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Preencha o título e a mensagem");
      return;
    }

    setIsSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        toast.error("Você precisa estar logado");
        return;
      }

      const response = await fetch(
        "https://rrybrsyrtmybarwussul.supabase.co/functions/v1/send-push-notification",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ title, body }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Erro ao enviar notificação");
      }

      toast.success(`Notificação enviada para ${result.sent} dispositivos`);
      setTitle("");
      setBody("");
      refetch();
    } catch (error: any) {
      console.error("Error sending notification:", error);
      toast.error(error.message || "Erro ao enviar notificação");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        {/* Send Notification Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Enviar Notificação
            </CardTitle>
            <CardDescription>
              Envie uma notificação push para todos os usuários registrados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>{tokenCount || 0} dispositivos registrados</span>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Título da notificação"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={50}
              />
              <Textarea
                placeholder="Mensagem da notificação"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                maxLength={200}
              />
              <Button
                onClick={handleSendNotification}
                disabled={isSending || !title.trim() || !body.trim()}
                className="w-full"
              >
                <Send className="w-4 h-4 mr-2" />
                {isSending ? "Enviando..." : "Enviar Notificação"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notification History Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico de Notificações
            </CardTitle>
            <CardDescription>
              Últimas 20 notificações enviadas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {notifications && notifications.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Enviados</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.map((notification) => (
                      <TableRow key={notification.id}>
                        <TableCell className="font-medium">
                          {notification.title}
                        </TableCell>
                        <TableCell>{notification.recipient_count}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(notification.sent_at!), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhuma notificação enviada ainda
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
